import React from 'react';
import cx from 'classnames';
import { UI_CROSS } from '@bwoty-web/ui-kit/icons';
import { useIsMobile } from '@bwoty-web/ui-kit/hooks';
import { HotelCard, Icon, isTheBigFourConcept } from '@bwoty-web/ui-kit';

import { useFavorites } from '../../hooks/useFavorites';
import { useInitialState } from '../../hooks/useInitialState';

import './hotelHitOrigo.scss';

const getIncludedAncillary = (webuiHotelHitTexts, mealAncillary) => {
  if (!Array.isArray(webuiHotelHitTexts?.mealAncillary)) {
    return {
      mealAncillary: null,
      mealAncillaryExtension: null,
    };
  }
  const included = webuiHotelHitTexts?.mealAncillary.find(
    (caAncillary) => caAncillary?.value === mealAncillary,
  );
  if (included && included.name && webuiHotelHitTexts.mealAncillaryExtension) {
    return {
      mealAncillary: included.name,
      mealAncillaryExtension: `${webuiHotelHitTexts.mealAncillaryExtension}.`,
    };
  }
  return {
    mealAncillary: null,
    mealAncillaryExtension: null,
  };
};

const HotelHitOrigo = (props) => {
  const {
    hotelUrl,
    isMapView,
    onClose,
    onSelect,
    openPriceDetails,
    origoData,
    paxLabel,
    webuiHotelHitTexts,
  } = props;
  const isMobile = useIsMobile();
  const { isFavorite, onChangeFavorite } = useFavorites(origoData);
  const { featureToggles } = useInitialState();

  if (!origoData || !webuiHotelHitTexts) {
    return null;
  }
  const {
    accommodations,
    content: origoContent,
    lolloAndBernie,
    mainCategory,
    priceOffer,
  } = origoData;

  const tags = [];
  if (origoContent?.productInfo?.isAdultHotel && webuiHotelHitTexts?.adultFriendly) {
    tags.push({ text: webuiHotelHitTexts?.adultFriendly, theme: 'adult' });
  } else if (origoContent?.productInfo?.isFamilyHotel && webuiHotelHitTexts?.childFriendly) {
    tags.push({ text: webuiHotelHitTexts?.childFriendly, theme: 'family' });
  }

  const conceptName = (mainCategory?.name || '').toLowerCase().replace(/(\s|\.|')/g, '');
  const isBigFourConcept = isTheBigFourConcept(conceptName);
  const conceptRowText = conceptName ? webuiHotelHitTexts?.[`concept_${conceptName}`] : '';

  const promotionLabel = origoData?.promotionLabels ? origoData.promotionLabels[0] : null;

  const includedAncillary = getIncludedAncillary(
    webuiHotelHitTexts,
    origoContent?.productInfo?.mealAncillary,
  );

  const packageDescriptionText =
    accommodations?.[0].rooms?.[0].boardCode !== 'N'
      ? accommodations?.[0].rooms?.[0].boardCodeDescription
      : null;

  const mapUspList = (quickFacts, mapView) => {
    return quickFacts
      ?.map((quickFact) => ({
        id: quickFact.id,
        name: quickFact.title,
        value: quickFact.displayValue,
      }))
      ?.filter(
        (usp) =>
          !mapView || (usp.id !== 'distanceToNearestBeach' && usp.id !== 'distanceToNearestCenter'),
      )
      ?.map((usp) => `${usp.name}: ${usp.value}`);
  };

  const getPackageInfoText = (content, packageTexts) => {
    const packageInfo =
      packageTexts?.packageInfoNewPriceCommunication || packageTexts?.packageInfo || '';
    const flightOnlyInfo =
      packageTexts?.flightOnlyInfoNewPriceCommunication || packageTexts?.flightOnlyInfo || '';
    const unSpecifiedInfo =
      packageTexts?.unSpecifiedInfoNewPriceCommunication || packageTexts?.unSpecifiedInfo || '';

    if (packageInfo || content?.isUnspecified) {
      if (!content?.isFlightOnly && !content?.isUnspecified) {
        return packageInfo;
      }

      if (content?.isFlightOnly && !content?.isUnspecified) {
        return flightOnlyInfo;
      }

      if (content?.isUnspecified) {
        return unSpecifiedInfo;
      }
    }

    return null;
  };

  const { district, resort, area } = origoContent?.geographical || {};

  const showArea = (area && !resort) || (area && resort && resort.name !== area.name);
  const subtitle = ''
    .concat(district ? `${district.name}, ` : '')
    .concat(resort ? `${resort.name}` : '')
    .concat(showArea ? `, ${area.name}` : '');

  const bulletIncludedAncillary =
    !!includedAncillary?.mealAncillary && !!includedAncillary?.mealAncillaryExtension;

  const hotelCardMapViewContent = {
    // Layout
    columnSpan: 4,
    // Functions
    onClick: onSelect,
    // Image
    image: {
      cloudinaryUriTemplate: origoContent?.listImage?.cloudinaryUriTemplate,
      size: {
        height: origoContent?.listImage?.height,
        width: origoContent?.listImage?.width,
      },
      alt: origoContent?.listImage?.alt,
    },
    concept: conceptName,
    tags: tags.map((tag) => tag.text),
    lolloBernie: !!lolloAndBernie,
    // HeaderContent
    rating: origoContent?.doubledRating,
    subtitle,
    title: origoContent?.name,
    url: hotelUrl,
    // BodyContent
    bullets: mapUspList(origoContent?.hotelListQuickFacts, isMapView),
    guestRating: origoContent?.guestRating || null,
    tripadvisorRating: origoContent?.tripAdvisor?.averageRating,
    ...(packageDescriptionText && {
      ksps: [packageDescriptionText],
    }),
    // FooterContent
    buttonText: webuiHotelHitTexts?.button,
    footerDescription: (
      <>
        {getPackageInfoText(origoContent, webuiHotelHitTexts)}
        <br />
        {paxLabel}
      </>
    ),
    originalPrice:
      priceOffer?.ordinaryPrice > priceOffer?.totalPrice ? priceOffer?.ordinaryPrice : null,
    price: priceOffer?.totalPrice !== 0 ? priceOffer?.totalPrice : null,
  };

  if (bulletIncludedAncillary)
    hotelCardMapViewContent.bullets.push(
      `${includedAncillary?.mealAncillary} ${includedAncillary?.mealAncillaryExtension}`,
    );
  if (isMapView) hotelCardMapViewContent.imagePosition = 'top';
  // eslint-disable-next-line no-underscore-dangle
  const hasGallery = typeof window !== 'undefined' && !!window?.__INITIAL_STATE_GALLERY__;

  const hotelCardListViewContent = {
    ...hotelCardMapViewContent,
    // Layout
    columnSpan: 12,
    imageWidth: 4,
    // Functions
    onClickGallery: hasGallery
      ? () => window.postMessage(`gallery?itemType=hotel&id=${origoContent?.wvId}`)
      : undefined,
    onClickPriceDetails: openPriceDetails,
    handleFavoriteChange: featureToggles?.heartMode ? onChangeFavorite : undefined,
    lazy: true,
    // Image
    conceptRowText,
    isFavorite,
    // // BodyContent - Not used currently for HotelFinder
    promotionLabel: {
      text: promotionLabel?.text,
      urgency: promotionLabel?.urgency,
      type: promotionLabel?.promotionType,
    },
    // // FooterContent - Not used currently for HotelFinder
    // scarcityText,
    pricePerPerson: priceOffer?.adultPrice !== 0 ? priceOffer?.adultPrice : null,
    imageGalleryCount: origoContent?.numberOfImagesAndVideos || 0,
    // discountDisplayType: 'discount',
    discountDisplayType: 'originalWithPercentage',
  };

  if (isBigFourConcept && isMapView) {
    hotelCardMapViewContent.concept = undefined;
  }

  return (
    <div className={cx('hf-hotel', { 'hf-hotel--map': isMapView })}>
      {isMapView && !isMobile && (
        <Icon
          alt="Close selected hotel card"
          className="hf-hotel-list-item-map__close-icon"
          onClick={onClose}
          onKeyUp={(e) => {
            if (e.key === 'Enter') onClose();
          }}
          path={UI_CROSS}
          size="l"
          tabIndex="0"
        />
      )}
      <HotelCard
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(isMapView ? hotelCardMapViewContent : hotelCardListViewContent)}
      />
    </div>
  );
};

export default HotelHitOrigo;
